import React, { useCallback, useEffect, useState } from "react";
import { Box, List, ListItem, Typography, InputLabel, Tooltip, IconButton, Card, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import Stack from "@mui/material/Stack";
import { produce } from "immer";
import { useTheme } from "@mui/material/styles";
import FollowButton from "../../components/followButton/followButton";
import { HTTPMethod, callApi as patientsOrderRequest } from "../../services/HttpService/HttpService";
import { OrdersComponentApi, PatientsDetailsApi } from "../../constants/ApiConstants";
import { useLocation, Link as RouterLink, useNavigate } from "react-router-dom";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import NoData from "../noData/noData";
import { LineOfBusinessEnum } from "../../constants/BusinessConstants";
import CustomTablePagination, { CustomPagination } from "../CustomTablePagination/CustomTablePagination";
import { EntityTypeForFollow, paceStatusCode, PriorityNameEnum, quoteStatus, statusEnum } from "../../common/enums";
import OrdersProofDetail from "../OrdersProofDetail/OrdersProofDetail";
import ReasonCode from "../../common/Utils/ReasonCode/ReasonCode";
import PriorityIcon from "../../common/Utils/PriorityIcon/PriorityIcon/PriorityIcon";
import ETA from "../ETA/ETA";
import MapLocationButton from "../MapLocationButton/MapLocationButton";
import OrderTypeText from "../../common/Utils/OrderTypeText/OrderTypeText";
import { mixedOrderTitle } from "../../common/Utils/CommonFunctions/CommonFunctions";
import OrderType from "../OrderType/OrderType";
import { useSelector } from "react-redux";
import ItemLabel from "../ItemLabel/ItemLabel";
import StatusBadge from "../StatusBadge/StatusBadge";
import PatientsOrderSkeleton from "./PatientsOrderSkeleton";
import Icon from "../Icon/Icon";

export const getOrderTitle = (mixOrderConfiguration, t, shouldWrapInSpan = true) => {
  let titleText;

  if (mixOrderConfiguration?.includes(","))
    return shouldWrapInSpan ? <span>{mixedOrderTitle(mixOrderConfiguration)}</span> : mixedOrderTitle(mixOrderConfiguration);
  else {
    switch (mixOrderConfiguration) {
      case "P":
        titleText = t("ProofTitles.proofOfPickup");
        break;
      case "SR":
        titleText = t("ProofTitles.proofOfService");
        break;
      default:
        titleText = t("ProofTitles.proofOfDelivery");
    }
    return shouldWrapInSpan ? <span>{titleText}</span> : titleText;
  }
};

const PatientsOrder = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [orderData, setOrdersData] = useState([]);
  const [proofData, setProofData] = useState(null);
  const location = useLocation();
  const [selectedValue, setSelectedValue] = useState("Most recent");
  const [sortedOrderData, setSortedOrderData] = useState(orderData);
  const { page, rowsPerPage, setPage, setRowsPerPage, handleChangePage, handleChangeRowsPerPage } = CustomPagination();
  const [isLoading, setLoading] = useState(true);
  const [isPaginationLoading, setPaginationLoading] = useState(false);
  let showMessage = `No orders exist for this ${LineOfBusinessEnum.patientToMember.toString().toLowerCase()}.`;
  const [proofOfDelivery, setProofOfDelivery] = useState(null);
  const userRoles = useSelector(state => state.authState.userData?.data?.user_info?.role);
  const isReadOnly = userRoles?.is_read_only ? true : false;

  const fetchOrdersData = useCallback(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const newData = orderData?.slice(startIndex, endIndex);
    setSortedOrderData(newData);
    setPaginationLoading(false);
  }, [page, orderData, rowsPerPage]);
  useEffect(() => {
    fetchOrdersData();
  }, [fetchOrdersData]);

  useEffect(() => {
    const fetchOrdersData = async () => {
      setLoading(true);
      const requestBody = location.pathname.split("/").pop();
      const apiEndpoint =
        PatientsDetailsApi.patientsOrderTab + "/" + requestBody + "/orders";

      const response = await patientsOrderRequest(
        HTTPMethod.Get,
        apiEndpoint
      );

      if (response && response.status_code === 200 && response.data) {
        setOrdersData(response.data?.orders);
        setLoading(false);
        setPaginationLoading(false);
      } else {
        setLoading(false);
        setPaginationLoading(false);
      }
    };
    fetchOrdersData();
  }, []);

  const fetchProofData = async (orderId) => {
    const proofResponse = await patientsOrderRequest(
      HTTPMethod.Get,
      OrdersComponentApi.orderProofOfDelivery.replace(
        "{order_id}",
        orderId
      )
    );
    if (proofResponse && proofResponse?.status_code == 200 && proofResponse?.data) {
      setProofData(proofResponse.data);
    }
  }

  const handleChange = (event) => {
    if (event) setSelectedValue(event.target.value);
    handleSortChange(event.target.value);
  };

  const handleSortChange = (sortType) => {
    setRowsPerPage(25);
    setPage(0);
    let sortedData = [...orderData];

    switch (sortType) {
      case "Oldest":
        sortedData = sortedData.reverse();
        break;
      case "Order number (ascending)":
        sortedData = sortedData.sort((a, b) => {
          return a.order_id - b.order_id;
        });
        break;
      case "Order number (descending)":
        sortedData = sortedData.sort((a, b) => {
          return b.order_id - a.order_id;
        });
        break;
      default:
        break;
    }

    setSortedOrderData(sortedData);
    setSelectedValue(sortType);
  };

  const handleApproveOrder = (e, order_id) => {
    e.preventDefault();
    localStorage.setItem('approveOrder', 'listing');
    navigate("/order-approvals/order-approval-details/" + order_id);
  }
  return (
    <>
      <Box data-testid="PatientsOrder">
        {sortedOrderData.length > 0 ? (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 6 }}>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="sort-by-label">Sort by</InputLabel>
                <Select
                  labelId="sort-by-label"
                  id="sort-by-select"
                  value={selectedValue}
                  label="Sort by"
                  onChange={handleChange}
                  data-testid="select"
                >
                  <MenuItem value="Most recent" data-testid="MostRecent">Most recent</MenuItem>
                  <MenuItem value="Oldest" data-testid="Oldest">Oldest</MenuItem>
                  <MenuItem value="Order number (ascending)" data-testid="ascending">Order number (ascending)</MenuItem>
                  <MenuItem value="Order number (descending)" data-testid="descending">Order number (descending)</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Card>
              <List disablePadding>
                {sortedOrderData?.map((item, index) => (
                  <React.Fragment key={item.order_id}>
                    <RouterLink
                      className="textDecoration"
                      to={item?.order_type === "Supplies" ? `/orders/supply-order-details/${item.order_id}` : `/orders/order-details/${item.order_id}`}
                    >
                      <ListItem data-testid="elem" sx={{ py: 5, alignItems: 'flex-start', flexDirection: 'column', gap: 2 }}>
                        <Stack direction="column" spacing={3} sx={{ width: '100%' }}>
                          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h5">{item.order_id}</Typography>
                            <Box
                              sx={{ mb: 3, visibility: (item.order_status !== 'Delivered' && item.order_status !== 'Canceled' && item.order_status !== 'Picked Up') ? 'visible' : 'hidden' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FollowButton
                                toggleValue={item?.followed}
                                toggleSetValue={(value) => setOrdersData(produce(draft => { draft[index].followed = value; }))}
                                followPayload={{
                                  entityId: item?.order_id,
                                  userId: [],
                                  entityType: item?.order_type === "Supplies" ? EntityTypeForFollow.SupplyOrder : EntityTypeForFollow.Order,
                                }}
                              />
                            </Box>
                          </Stack>
                          <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 3, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}>
                            <Stack direction="row" spacing={2}>
                              <StatusBadge status={item.order_status} />
                              {(item.order_status === statusEnum["On my way"] || item.order_status === statusEnum["En-Route"] || item.order_status === statusEnum["Picking Up"]) && (
                                <ETA orderStatus={item.order_status} arrivalTime={item?.arrival_time} estimatedArrivalTime={item?.estimated_arrival_time} exceptedArrivalTime={item?.excepted_arrival_time} exceptedExitTime={item?.excepted_exit_time} siteLocalTime={item?.site_local_time} isDelayed={item?.is_eta_delayed} />
                              )}
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={2}
                              sx={{ display: (item.order_status === statusEnum["On my way"] || item.order_status === statusEnum["En-Route"] || item.order_status === statusEnum["Picking Up"]) ? 'block' : 'none' }}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            >
                              <MapLocationButton orderStatus={item.order_status} orderId={item.order_id} latitude={item.latitude} longitude={item.longitude} addressLine1={item.address_line1} addressLine2={item.address_line2} city={item.city} state={item.state} zipcode={item.zip_code} country={item.country} arrivalTime={item?.arrival_time} estimatedArrivalTime={item?.estimated_arrival_time} exceptedArrivalTime={item?.excepted_arrival_time} exceptedExitTime={item?.excepted_exit_time} siteLocalTime={item?.site_local_time} isDelayed={item?.is_eta_delayed} />
                              {(item?.order_status === "Pending Approval" && item?.has_approval_access) && (
                                <Tooltip title={<span>Approve Order</span>}>
                                  <IconButton color="primary" onClick={(e) => handleApproveOrder(e, item?.order_id)}>
                                    <Icon icon="fa-clipboard-check" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {item?.is_proof_of_delivery && (
                                <Tooltip enterTouchDelay={0} title={getOrderTitle(item?.mix_order_configuration, t)}>
                                  <IconButton color="primary" data-testid={'receiptButton' + index}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (item?.mix_order_configuration?.includes(",")) {
                                        navigate("/orders/mix-order-proof", { state: { data: item } });
                                      } else {
                                        fetchProofData(item.order_id);
                                        setProofOfDelivery(item);
                                      }
                                    }}
                                  >
                                    <Icon icon="fa-images" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </Stack>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'space-between', width: '100%' }}>
                          <Stack spacing={1} sx={{ mb: { xs: 2, sm: 0 } }}>
                            {item.total_equipment !== 0 && (
                              <ItemLabel
                                label={item.order_type === 'Supplies' ? t("NewOrder.uniqueSupplyItems") : t("Dashboard.uniqueEquipment")}
                                text={item.total_equipment}
                              />
                            )}
                            {item.equipment_requiring_approval !== 0 && (
                              <ItemLabel label={t("OrdersTab.equipmentRequiringApproval")} text={item.equipment_requiring_approval.toString()} />
                            )}
                            <ItemLabel label={t("PatientDetails.provider")} text={item.provider[0].provider_name} />
                            <ItemLabel label={t("OrdersTab.orderedBy")} text={`${item.ordered_by} (${item.company_name})`} />
                            {item.ordered_on !== "01/01/0001 (12:00 AM)" && (
                              <ItemLabel label={t("EquipmentsTab.orderedOn")} text={item.ordered_on} />
                            )}
                            <OrderTypeText
                              orderType={item.order_type}
                              orderStatus={item.order_status}
                              deliveredOn={item.delivered_on}
                              deliverBy={item.deliver_by}
                              pickedUpOn={item.picked_up_on}
                              pickupBy={item.pickup_by}
                              servicedOn={item.serviced_on}
                              serviceBy={item.service_by}
                              mixOrderConfiguration={item?.mix_order_configuration}
                            />
                          </Stack>
                          <Stack sx={{ alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: { xs: 0, sm: 4 } }}>


                          </Stack>
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{ marginTop: 5, alignItems: 'baseline', width: { xs: '100%', sm: 'fit-content' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
                          >
                            <Stack direction="column" sx={{ alignItems: 'center' }}>
                              {PriorityIcon(PriorityNameEnum[item?.order_priority])}
                            </Stack>
                            {item?.reason_code && (
                              <Stack direction="column" sx={{ alignItems: 'center' }}>
                                {ReasonCode(item?.reason_code, item?.order_type)}
                              </Stack>
                            )}
                            {!item?.order_status?.includes(paceStatusCode.providerQuote) && !item?.order_status?.includes(paceStatusCode.clientReview) && !item?.order_status?.includes(paceStatusCode.stateServeAudit) && (
                              <OrderType mixOrderConfiguration={item?.mix_order_configuration} />
                            )}
                          </Stack>
                        </Stack>
                      </ListItem>
                    </RouterLink>
                    {index < sortedOrderData.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Card>
            <div
              style={{
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                fontSize: "var(--common-heading)",
                marginTop: "30px",
              }}
            >
              <CustomTablePagination
                totalRecord={orderData.length}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                isLoading={isPaginationLoading}
              />
            </div>
          </Box>

        ) : isLoading ? (
          <PatientsOrderSkeleton />
        ) : (
          <Card>
            <NoData message={showMessage} />
          </Card>
        )}
      </Box>
      {proofData && Object.keys(proofData).map((proofKey) => {
        if (proofData[proofKey]) {
          return (
            <OrdersProofDetail
              key={proofKey}
              proofData={proofOfDelivery?.mix_order_configuration?.includes(",") ? proofOfDelivery[proofKey] : proofData[proofKey]}
              setProofData={setProofData}
            />
          );
        }
        return null;
      })}
    </>
  );
};

export default PatientsOrder;
